import { EntityInfo, EntityInfoUtils } from '@record-it-npm/synchro-common';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { EntityStorage } from '../EntityStorage/EntityStorage';
import { HooksContainer } from '../HooksContainer/HooksContainer';
import { IdGenerator } from '../IdGenerator/IdGenerator';
import { EntityCollection } from './EntityCollection';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityByEntityNameFromEntityManagerEntityTypesByEntityName } from '../EntityByEntityNameFromEntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import { VisibleEntities } from './VisibleEntities';
import { EntityFilterContainer } from '../EntityFilter';

export class EntityRepository<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> {
  private static initializedRepositoriesByEntityName: Record<
    string,
    EntityRepository<any, any, any> | null
  > = {};

  private readonly entityCollection: EntityCollection<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >;

  private readonly hooksContainer: HooksContainer<
    EntityRepositoryHooks<TEntityTypesByEntityName[TEntityName]['entity']>
  > = new HooksContainer();

  private readonly visibleEntities: VisibleEntities<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >;

  private initialized: boolean = false;

  constructor(
    private readonly entityInfo: EntityInfo<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
      TEntityName,
      TEntityTypesByEntityName[TEntityName]['entity']
    >,
    private readonly options: EntityRepositoryOptions<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ) {
    this.entityCollection = new EntityCollection(
      entityInfo,
      options.entityStorage,
      { preserveInstances: options.preserveEntityInstances }
    );

    this.visibleEntities = new VisibleEntities<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >({
      entityInfo,
      filterContainer: options.filterContainer,
      onEntityVisibilityChanged: ({ entity }) => {
        this.hooksContainer.callHooks('afterEntityVisibilityChanged', entity);
      }
    });
  }

  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.entityCollection.loadEntities({
      patchEntity: this.patchLoadedEntity.bind(this)
    });

    this.visibleEntities.init({
      allEntitiesById: this.entityCollection.getAllById()
    });

    if (
      EntityRepository.initializedRepositoriesByEntityName[
        this.entityInfo.entityName
      ]
    ) {
      // because they would generate the same ids since they are not synchronized
      throw new Error(
        `only one EntityRepository for "${this.entityInfo.entityName}" is allowed to be initialized`
      );
    }
    EntityRepository.initializedRepositoriesByEntityName[
      this.entityInfo.entityName
    ] = this;

    this.initialized = true;
  }

  public destroy(): void {
    this.initialized = false;

    this.entityCollection.clear();

    this.visibleEntities.destroy();

    EntityRepository.initializedRepositoriesByEntityName[
      this.entityInfo.entityName
    ] = null;
  }

  public getEntityInfo(): EntityInfo<
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
    TEntityName,
    TEntityTypesByEntityName[TEntityName]['entity']
  > {
    return this.entityInfo;
  }

  public getByIdOrOriginalId(
    anyId: EntityManagerBaseEntity['id']
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertInitialized();

    return this.visibleEntities.getByIdOrOriginalId(anyId);
  }

  public getByOriginalId(
    originalId: EntityManagerBaseEntity['id']
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertInitialized();

    return this.visibleEntities.getByOriginalId(originalId);
  }

  public getRequiredById(
    id: string
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    const entity = this.getById(id);
    assertNotNullOrUndefined(
      entity,
      `no ${this.entityInfo.entityName} found for id "${id}"`
    );
    return entity;
  }

  public getById(
    id: string
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertInitialized();

    return this.visibleEntities.getById(id);
  }

  public getByIds(
    ids: Array<string>
  ): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    this.assertInitialized();

    const entities = ids.map((id) => this.visibleEntities.getById(id));
    return entities.filter(
      (e): e is TEntityTypesByEntityName[TEntityName]['entity'] => !!e
    );
  }

  public getByTemporaryGroupName(
    temporaryGroupName: string
  ): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    this.assertInitialized();

    return this.getAll().filter(
      (x) => x.temporaryGroupName === temporaryGroupName
    );
  }

  public getAll(): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    this.assertInitialized();

    return this.visibleEntities.getAll();
  }

  public getByIdOrOriginalIdIncludingInvisibleEntities(
    anyId: EntityManagerBaseEntity['id']
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertInitialized();

    return this.entityCollection.getByIdOrOriginalId(anyId);
  }

  public getByOriginalIdIncludingInvisibleEntities(
    originalId: EntityManagerBaseEntity['id']
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertInitialized();

    return this.entityCollection.getByOriginalId(originalId);
  }

  public getRequiredByIdIncludingInvisibleEntities(
    id: string
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    const entity = this.getByIdIncludingInvisibleEntities(id);
    assertNotNullOrUndefined(
      entity,
      `no ${this.entityInfo.entityName} found for id "${id}"`
    );
    return entity;
  }

  public getByIdIncludingInvisibleEntities(
    id: string
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertInitialized();

    return this.entityCollection.getById(id);
  }

  public getByIdsIncludingInvisibleEntities(
    ids: Array<string>
  ): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    this.assertInitialized();

    const entities = ids.map((id) => this.entityCollection.getById(id));
    return entities.filter(
      (e): e is TEntityTypesByEntityName[TEntityName]['entity'] => !!e
    );
  }

  public getByTemporaryGroupNameIncludingInvisibleEntities(
    temporaryGroupName: string
  ): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    this.assertInitialized();

    return this.getAllIncludingInvisibleEntities().filter(
      (x) => x.temporaryGroupName === temporaryGroupName
    );
  }

  public getAllIncludingInvisibleEntities(): Array<
    TEntityTypesByEntityName[TEntityName]['entity']
  > {
    this.assertInitialized();

    return this.entityCollection.getAll();
  }

  /**
   * Since there is an limitation for assertions functions
   *
   * @param entity
   * @param errorMessagePrefix - example: "can\'t Worker.handleInput because processTaskGroup"
   */
  public assertEntityIsTracked(
    entity: EntityManagerBaseEntity | null,
    errorMessagePrefix: string
  ): asserts entity is EntityManagerBaseEntity {
    this.entityCollection.assertEntityIsTracked(entity, errorMessagePrefix);
  }

  public entityIsTracked(
    entity: TEntityTypesByEntityName[TEntityName]['entity'] | null
  ): boolean {
    return this.entityCollection.entityIsTracked(entity);
  }

  public create(
    creationEntity: TEntityTypesByEntityName[TEntityName]['creationEntity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const entity = {
      ...this.prepareEntity(creationEntity),
      id: this.options.idGenerator.next(),
      onlyLocal: true,
      originalIds: []
    } as TEntityTypesByEntityName[TEntityName]['entity'];

    this.hooksContainer.callHooks('beforeEntityCreated', entity);
    const addedEntity = this.entityCollection.add(entity);
    this.hooksContainer.callHooks('afterEntityCreated', addedEntity);

    this.visibleEntities.addEntity(addedEntity);

    return addedEntity;
  }

  public delete(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const entityToDelete = this.options.preserveEntityInstances
      ? entity
      : { ...entity };
    entityToDelete.deleted = true;
    entityToDelete.deletedAt = new Date().toISOString();

    this.entityCollection.remove(entityToDelete);
    this.hooksContainer.callHooks('afterEntityDeleted', entityToDelete);

    this.visibleEntities.removeEntity(entityToDelete);

    return entityToDelete;
  }

  public add(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const addedEntity = this.entityCollection.add(entity);
    this.hooksContainer.callHooks('afterEntityUpdated', addedEntity);

    this.visibleEntities.addEntity(addedEntity);

    return addedEntity;
  }

  public update(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const updatedEntity = this.entityCollection.update(entity);
    this.hooksContainer.callHooks('afterEntityUpdated', updatedEntity);

    this.visibleEntities.updateEntity(updatedEntity);

    return updatedEntity;
  }

  public addLocally(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const addedEntity = this.entityCollection.add(entity);
    this.hooksContainer.callHooks('afterEntityUpdatedLocally', entity);

    this.visibleEntities.addEntity(addedEntity);

    return addedEntity;
  }

  public updateLocally(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const updatedEntity = this.entityCollection.update(entity);
    this.hooksContainer.callHooks('afterEntityUpdatedLocally', updatedEntity);

    this.visibleEntities.updateEntity(updatedEntity);

    return updatedEntity;
  }

  public addExternalEntityLocally(
    entity: Partial<TEntityTypesByEntityName[TEntityName]['entity']>
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertInitialized();

    const addedEntity = this.entityCollection.addExternalEntity(entity);
    this.hooksContainer.callHooks('afterEntityUpdatedLocally', addedEntity);

    this.visibleEntities.addEntity(addedEntity);

    return addedEntity;
  }

  public removeLocally(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): void {
    this.assertInitialized();

    this.entityCollection.remove(entity);
    this.hooksContainer.callHooks('afterEntityRemovedLocally', entity);

    this.visibleEntities.removeEntity(entity);
  }

  public registerHooks(
    hooks: EntityRepositoryHooks<
      TEntityTypesByEntityName[TEntityName]['entity']
    >
  ): Disposable {
    return this.hooksContainer.registerHooks(hooks);
  }

  public createShadowEntitiesWithTemporaryGroupName(
    temporaryGroupName: string,
    newTemporaryGroupName: string | null = null
  ): void {
    this.assertInitialized();
    const entities =
      this.getShadowEntitiesWithTemporaryGroupName(temporaryGroupName);

    for (const entity of entities) {
      this.update({
        ...entity,
        shadowEntity: false,
        temporaryGroupName: newTemporaryGroupName
      });
    }
  }

  public clearShadowEntitiesWithTemporaryGroupName(
    temporaryGroupName: string
  ): void {
    this.assertInitialized();

    const entities =
      this.getShadowEntitiesWithTemporaryGroupName(temporaryGroupName);

    for (const entity of entities) {
      this.removeLocally(entity);
    }
  }

  protected patchLoadedEntity(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: PatchLoadedEntityOptions<
      TEntityTypesByEntityName[TEntityName]['entity']
    >
  ): void {}

  private prepareEntity(
    creationEntity: TEntityTypesByEntityName[TEntityName]['creationEntity']
  ): Omit<TEntityTypesByEntityName[TEntityName]['entity'], 'id'> {
    // in my opionion we shouldn't need to cast this as any, but the new typescripts opinion is different than mine
    const entity: Partial<TEntityTypesByEntityName[TEntityName]['entity']> = {
      ...creationEntity
    } as any;

    EntityInfoUtils.applyDefaultValues<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
      TEntityName,
      EntityByEntityNameFromEntityManagerEntityTypesByEntityName<TEntityTypesByEntityName>
    >(this.entityInfo, entity);

    EntityInfoUtils.validateEntityWithoutId<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
      TEntityName,
      EntityByEntityNameFromEntityManagerEntityTypesByEntityName<TEntityTypesByEntityName>
    >(this.entityInfo, entity);

    return entity;
  }

  private getShadowEntitiesWithTemporaryGroupName(
    temporaryGroupName: string
  ): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    return this.getAllIncludingInvisibleEntities().filter(
      (entity) =>
        entity.shadowEntity && entity.temporaryGroupName === temporaryGroupName
    );
  }

  private assertInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        `can't use the EntityRepository for "${this.entityInfo.entityName}" because it isn't initialized`
      );
    }
  }
}

export type EntityRepositoryHooks<TEntity extends EntityManagerBaseEntity> = {
  /**
   * If you intend to modify the entity, just modify it in place (the entity isn't tracked yet).
   * Do not use the `update` functions after modifying the entity.
   * @param entity
   */
  beforeEntityCreated?(entity: TEntity): void;
  afterEntityCreated?(entity: TEntity): void;
  afterEntityUpdated?(entity: TEntity): void;
  afterEntityUpdatedLocally?(entity: TEntity): void;
  afterEntityDeleted?(entity: TEntity): void;
  afterEntityRemovedLocally?(entity: TEntity): void;
  afterEntityVisibilityChanged?(entity: TEntity): void;
};

export type EntityRepositoryOptions<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  entityStorage: EntityStorage<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  idGenerator: IdGenerator;
  filterContainer: EntityFilterContainer<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  preserveEntityInstances: boolean;
};

export type PatchLoadedEntityOptions<TEntity extends EntityManagerBaseEntity> =
  {
    entity: TEntity;
  };
